import { Grid, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import { Table, Input, ConfigProvider, Select, Space, Badge } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import moment from "moment";
import useDebounce from "hooks/useDebounce";
import { getAllLogs } from "store";
import { EmptyData } from "components/mnl/global/emptyData";
import { useCurrentHeight } from "utils/getScreenSize";
import { useCallback, useEffect, useState } from "react";
import LogDetails from "./logDetails";

const GetAllLogs = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const debouncedInputValue = useDebounce(searchText);
  const [sortStr, setSortStr] = useState("timeStamp");
  const [sortDirection, setSortDirection] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [clientData, setClientData] = useState([]);
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(1);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [selectedErrorDetails, setSelectedErrorDetails] = useState([]);
  const [modalTitle, setModalTitle] = useState("");

  const LoadData = useCallback(() => {
    //HANDLE LOADING
    if (getAllLogs.pending().type === "logs/get-all-logs/pending") {
      setIsLoading(true);
    }

    dispatch(
      getAllLogs({
        search: debouncedInputValue,
        pageSize: pageSize,
        pageNumber: current,
        sortField: sortStr,
        sortDirection: sortDirection,
      })
    ).then((element) => {
      if (element.type === "logs/get-all-logs/fulfilled") {
        setIsLoading(false);
        setClientData(element.payload.data.items);
        setTotal(element.payload.data.totalPages * pageSize);
      }
    });
  }, [
    dispatch,
    debouncedInputValue,
    pageSize,
    sortStr,
    current,
    sortDirection,
  ]);

  useEffect(() => {
    LoadData();
  }, [dispatch, LoadData]);

  const onChange = (page) => {
    setCurrent(page);
  };
  const onShowSizeChange = (current, pageSize) => {
    setPageSize(pageSize);
  };
  const handleChangeSearch = (val) => {
    setIsLoading(true);
    setSearchText(val.target.value);
    setCurrent(1);
  };

  const renderEmptyData = () => (
    <EmptyData
      description="No Logs Detected"
      action={() => {
        return;
      }}
      btnTitle=""
      renderButton={false}
    />
  );

  const handleChangeSortField = (value) => {
    setSortStr(value);
  };

  const handleChangeSortDirection = (value) => {
    setSortDirection(value);
  };

  const handleOpenModal = (selectedError) => {
    setSelectedErrorDetails(selectedError);
    setOpenErrorModal(true);
  };

  const handleCloseModal = () => {
    setSelectedErrorDetails([]);
    setOpenErrorModal(false);
    setModalTitle("");
  };

  const columns = [
    {
      title: "Id",
      dataIndex: "id",
      key: "id",
      width: 40,
    },
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      width: 70,
    },
    {
      title: "Message",
      dataIndex: "message",
      key: "message",
      width: 300,
    },
    {
      title: "Exception",
      width: 90,
      render: (rec) => (
        <>
          {rec.exception !== null && (
            <Space>
              <Badge status="error" />
              <Typography
                sx={{
                  cursor: "pointer",
                }}
                onClick={() => {
                  handleOpenModal(rec);
                  setModalTitle("Exception");
                }}
                variant="p"
              >
                View Exception
              </Typography>
            </Space>
          )}
        </>
      ),
    },
    {
      title: "Log Event",
      width: 90,
      render: (rec) => (
        <>
          {rec.logEvent !== null && (
            <Space>
              <Badge status="error" />
              <Typography
                sx={{
                  cursor: "pointer",
                }}
                onClick={() => {
                  handleOpenModal(rec);
                  setModalTitle("Log Event");
                }}
                variant="p"
              >
                View Log Event
              </Typography>
            </Space>
          )}
        </>
      ),
    },
    {
      title: "Level",
      dataIndex: "level",
      key: "level",
      width: 80,
    },
    {
      title: "Time Stamp",
      dataIndex: "timeStamp",
      key: "timeStamp",
      width: 70,
      render: (rec, row) => {
        return moment(rec).format("MMMM DD,YYYY hh:mm");
      },
    },
  ];
  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3} lg={3}>
          <Input
            size=""
            placeholder="Search Logs"
            prefix={<SearchOutlined />}
            onChange={handleChangeSearch}
            value={searchText}
          />
        </Grid>
        <Grid item xs={12} md={3} lg={3}>
          <Select
            defaultValue={sortStr}
            onChange={handleChangeSortField}
            style={{
              width: 120,
            }}
            options={[
              {
                value: "id",
                label: "Id",
              },
              {
                value: "message",
                label: "Message",
              },
              {
                value: "user",
                label: "User",
              },
              {
                value: "level",
                label: "Level",
              },
              {
                value: "timeStamp",
                label: "time Stamp",
              },
            ]}
          />
          <Select
            defaultValue={sortDirection}
            onChange={handleChangeSortDirection}
            style={{
              width: 120,
              marginLeft: 2,
            }}
            options={[
              {
                value: 0,
                label: "Ascending",
              },
              {
                value: 1,
                label: "Descending ",
              },
            ]}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={6}></Grid>
      </Grid>
      <ConfigProvider renderEmpty={renderEmptyData}>
        <Table
          rowClassName="odd:bg-slate-300"
          columns={columns}
          dataSource={clientData}
          loading={isLoading}
          size="small"
          pagination={{
            total: total,
            showSizeChanger: true,
            onShowSizeChange: onShowSizeChange,
            current: current,
            onChange: onChange,
            showQuickJumper: true,
            position: ["bottomRight"],
          }}
          bordered={true}
          scroll={{ y: useCurrentHeight() - 330 }}
          style={{ marginTop: 10 }}
        />
      </ConfigProvider>
      <LogDetails
        openErrorModal={openErrorModal}
        handleOpenModal={handleOpenModal}
        handleCloseModal={handleCloseModal}
        selectedErrorDetails={selectedErrorDetails}
        modalTitle={modalTitle}
      />
    </>
  );
};
export default GetAllLogs;
